import type { WebTemplateHref, WebTemplateKey } from '@generalTypes/apiTypes';
import { loadWebConfigurations } from '@newStore/documentApi/documentApiState';
import { loadSharedItemFacets } from '@newStore/externalData/sharedItemFacets';
import { referenceFrames } from '@newStore/referenceFrames/referenceFrames';
import { loadReferenceFrame } from '@newStore/referenceFrames/referenceFramesState';
import { selectEmptyThemesRule } from '@newStore/validation/validationRules/notEmptyThemesValidationRules';
import type { RootNodeConfig } from '@nodeTypeConfig/configTypes';
import { authorsComponent } from '@nodeTypeConfig/helpers/editComponents/authorsComponent';
import { publishComponent } from '@nodeTypeConfig/helpers/editComponents/publishComponent';
import { titleComponent } from '@nodeTypeConfig/helpers/editComponents/titleComponent';
import * as ACTION_TYPES from '@store/constants/actionTypes';

import { selectTitleWithDescriptionNode } from '@newStore/documentUI/transformContent/genericSelectors';
import { selectCurriculumThemesValidation } from '@newStore/validation/validationRules/curriculumThemesConsistencyValidation';
import { curriculumSelectorComponent } from '@nodeTypeConfig/helpers/editComponents/curriculumSelector';
import { introDescriptionComponent } from '@nodeTypeConfig/helpers/editComponents/descriptionComponent';
import { proContentTypeForDatabasePicker } from '@nodeTypeConfig/helpers/editComponents/proContentTypeForDatabasePicker';
import { proLinks } from '@nodeTypeConfig/helpers/editComponents/proLinksAndMenuReferences';
import { proThemeAudienceTab } from '@nodeTypeConfig/helpers/editComponents/proThemeAudienceTab';
import { proThumbnailComponent } from '@nodeTypeConfig/helpers/editComponents/proThumbnailComponent';
import { webFacetsPlaceHolderComponent } from '@nodeTypeConfig/helpers/editComponents/proWebFacetComponent';
import { asideWebBasicConfiguration } from '@nodeTypeConfig/helpers/editComponents/webConfigComponents';
import { webSitesProHref } from '../../../../reduxLoop/api/apiRoutes';
import constants from '../../../../reduxLoop/constants/constants';
import { settings } from '../../../settings';
import { proDefaultBuildingBlocks } from '../../helpers/buildingBlocks/proDefaultBuildingBlocks';
import { leerplanSitesThemeComponent } from './leerplansitesGenericConfigProps';

export const SHARED_MINI_DATABASE_ITEM: RootNodeConfig = {
  nodeSelector: selectTitleWithDescriptionNode,
  validationRules: [selectCurriculumThemesValidation],
  information: {
    definiteArticle: true,
    single: 'Gedeeld databankitem',
    plural: 'Gedeelde databankitems',
    category: 'LEERPLANSITES_SO',
  },
  preloadActions: [
    {
      type: ACTION_TYPES.GET_REFERENCE_FRAME,
      payload: { key: constants.contentTypesKey },
    },
  ],
  onLoadActions: [
    loadWebConfigurations(),
    loadReferenceFrame({ referenceFrame: referenceFrames.pro }),
    loadSharedItemFacets({
      matchingParams: {
        themesMatchingOnRoot: { referenceFrameKey: constants.dienstverleningKovKey },
        themesOverlaps: { referenceFrameKey: constants.contentTypesKey },
        'webPage.template': '34371fa8-c06b-4ace-83ad-180e5cd0b50b' as WebTemplateKey, // MINI_DATABASE
        $$includeWebPages: true,
      },
    }),
  ],
  websiteEdition: [
    {
      domain: settings.domains.pro,
    },
  ],
  webconfiguration: {
    type: 'MINI_DATABASE_ITEM',
    website: {
      href: webSitesProHref,
    },
    template: {
      href: '/web/templates/21363fa8-c06b-4ace-92ad-180e5cd0c41a' as WebTemplateHref,
    },
  },
  websiteHref: webSitesProHref,
  tocTypes: [],
  buildingBlocks: proDefaultBuildingBlocks,
  createModal: {
    authors: {
      addUser: false,
      queryParams: constants.allAuthorsParameters,
    },
  },
  edit: [
    titleComponent,
    {
      ...introDescriptionComponent,
      customRequiredMessage:
        'Je moet de <strong>inleiding</strong> invullen. (Belangrijk: de zoeker toont de inleiding in de zoekresultaten)',
    },
    proThumbnailComponent,
    {
      ...authorsComponent,
      options: {
        queryParams: constants.allAuthorsParameters,
      },
    },
    publishComponent,
    {
      ...leerplanSitesThemeComponent,
      customRequiredMessage:
        'Je moet een <strong>leerplanthema</strong> invullen. Anders verschijnt dit item niet in een databank.',
    },
    curriculumSelectorComponent,
    {
      ...proContentTypeForDatabasePicker,
      label: 'In welke databanktype(s) moet dit item getoond worden?',
      customRequiredMessage:
        'Je moet een <strong>databanktype</strong> invullen. Anders verschijnt dit item niet in een databank.',
      validations: [selectEmptyThemesRule],
    },
    proLinks,
    webFacetsPlaceHolderComponent,
  ],
  audienceTab: proThemeAudienceTab,
  webTab: asideWebBasicConfiguration,
  isCreatable: true,
  isSearchable: true,
  isCloneable: true,
  allowSuggestions: true,
  isImportable: true,
  globalAttachmentsGroupContainer: true,
};
